// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import { createdAtField, getCreatedAtSort, idField } from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import LocalizedString from './LocalizedString';

class Organization implements StoredEntity {
    static classInternalName = 'organization';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        name: {
            displayName: 'Name',
            recordName: 'name',
            dataType: {
                primitive: 'simple-entity',
                isArray: true,
                extraData: {
                    model: () => LocalizedString,
                },
            },
            supportsQuery: false,
            supportsSort: false,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        phone: {
            displayName: 'Phone',
            recordName: 'phone',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        defaultPreAuthAmount: {
            displayName: 'Default PA Amount',
            recordName: 'default_pre_auth_amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        preAuthStep: {
            displayName: 'PA Step',
            recordName: 'pre_auth_step',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        licenseType: {
            displayName: 'License Type',
            recordName: 'license_type',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    1: 'Standard',
                    2: 'Plus',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        licenseExpiration: {
            displayName: 'License Expiration',
            recordName: 'license_expiration',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        defaultBeaconMinor: {
            displayName: 'Default Beacon Major',
            recordName: 'default_beacon_major',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        enabled: {
            displayName: 'Enabled',
            recordName: 'enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        rewardThreshold: {
            displayName: 'Reward Threshold',
            recordName: 'reward_threshold',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        rewardAmount: {
            displayName: 'Reward Amount',
            recordName: 'reward_amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        rewardsEnabled: {
            displayName: 'Rewards Enabled',
            recordName: 'rewards_enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        subThreshold: {
            displayName: 'Sign Up Bonus Threshold',
            recordName: 'sub_threshold',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        subAmount: {
            displayName: 'Sign Up Bonus Amount',
            recordName: 'sub_amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        subEnabled: {
            displayName: 'Sign Up Bonus Applicable',
            recordName: 'sub_enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: true,
            isOptional: false,
            displayOnMain: false,
        },
        digitalFeeBps: {
            displayName: 'Partner Digital Fee (bps)',
            recordName: 'digital_fee_bps',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        processorFeeBpsEstimate: {
            displayName: 'Processor Fee Estimate (bps)',
            recordName: 'processor_fee_bps_estimate',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        payfactoRevShareBps: {
            displayName: 'PayFacto Rev Share (bps)',
            recordName: 'payfacto_rev_share_bps',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        premiumFeatureFee: {
            displayName: 'Premium Feature Fee (CAD)',
            recordName: 'premium_feature_fee',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        premiumFeatureLabel: {
            displayName: 'Premium Feature Label',
            recordName: 'premium_feature_label',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
    };

    static rootEndpoint = 'organizations';

    static endpoints: Endpoints = constructEndpoints(Organization.rootEndpoint);

    static fileFields: EntityFieldCollection = {
        logo: {
            displayName: 'Logo',
            fileType: 'img',
            fileName: `logo`,
            urlName: 'logo',
            isArray: false,
            displayOnMain: true,
        },
    };

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[Organization.fields.id.recordName];
    }

    get displayName() {
        let name = this.id;
        const defaultLoc = this.record[Organization.fields.name.recordName].filter(
            (loc) => loc[LocalizedString.fields.languageCode.recordName] === 'en',
        );
        if (defaultLoc) name = defaultLoc[0].text;
        return name;
    }

    constructor(record: any) {
        this.record = record;
    }
}

export default Organization;
